import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { 
  User, 
  Camera, 
  MapPin, 
  Briefcase, 
  Phone, 
  Mail, 
  Globe, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Twitter,
  Youtube,
  Save,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Crown,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const businessCategories = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'hair_styling', label: 'Hair Styling & Beauty' },
  { value: 'chef_services', label: 'Chef & Culinary Services' },
  { value: 'fitness_training', label: 'Fitness & Personal Training' },
  { value: 'digital_marketing', label: 'Digital Marketing' },
  { value: 'consulting', label: 'Business Consulting' },
  { value: 'e_commerce', label: 'E-Commerce & Online Retail' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'education', label: 'Education & Training' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'automotive', label: 'Automotive Services' },
  { value: 'home_services', label: 'Home & Property Services' },
  { value: 'photography', label: 'Photography & Videography' },
  { value: 'event_planning', label: 'Event Planning' },
  { value: 'travel_tourism', label: 'Travel & Tourism' },
  { value: 'technology', label: 'Technology & IT Services' },
  { value: 'retail', label: 'Retail & Sales' },
  { value: 'restaurant_food', label: 'Restaurant & Food Service' },
  { value: 'legal_services', label: 'Legal Services' },
  { value: 'other', label: 'Other' }
];

const countries = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'IN', label: 'India' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' }
];

const socialPlatforms = [
  { key: 'website_url', label: 'Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
  { key: 'facebook_url', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourpage' },
  { key: 'instagram_url', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourusername' },
  { key: 'linkedin_url', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/yourprofile' },
  { key: 'twitter_url', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/yourusername' },
  { key: 'youtube_url', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/c/yourchannel' }
];

const MemberProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basics');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(`${API_BASE_URL}/api/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setProfile(prev => ({ ...prev, ...updates }));
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be less than 3MB');
      return;
    }

    try {
      // Get Cloudinary signature
      const token = localStorage.getItem('auth_token');
      const signatureResponse = await axios.post(`${API_BASE_URL}/api/profile/avatar/signature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (signatureResponse.data.ok) {
        // For now, we'll create a preview URL and save it
        // In production, this would upload to Cloudinary
        const previewUrl = URL.createObjectURL(file);
        
        await updateProfile({
          profile_photo_url: previewUrl
        });
        
        toast.success('Avatar updated successfully');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 'freebie': return 'bg-gray-100 text-gray-800';
      case 'rookie': return 'bg-blue-100 text-blue-800';
      case 'rookie_pro': return 'bg-green-100 text-green-800';
      case 'professional_builder': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 'enterprise': return <Crown className="w-4 h-4" />;
      case 'professional_builder': return <Star className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="h-96 bg-gray-200 rounded-lg"></div>
          <div className="h-96 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h2>
        <p className="text-gray-600">Unable to load your profile. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        <Badge className={`${getRankColor(profile.rank)} flex items-center space-x-1`}>
          {getRankIcon(profile.rank)}
          <span>{profile.rank?.replace('_', ' ').toUpperCase()}</span>
        </Badge>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'basics', label: 'Basics & Photo', icon: User },
            { id: 'business', label: 'Business Info', icon: Briefcase },
            { id: 'social', label: 'Social Links', icon: Globe },
            { id: 'privacy', label: 'Privacy Settings', icon: Eye }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Photo & Basics */}
        {activeTab === 'basics' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Camera className="w-5 h-5" />
                  <span>Profile Photo</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="relative inline-block">
                  <Avatar className="w-32 h-32">
                    <AvatarImage src={profile.profile_photo_url} />
                    <AvatarFallback className="text-2xl">
                      {profile.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <label className="absolute bottom-0 right-0 bg-yellow-400 p-2 rounded-full cursor-pointer hover:bg-yellow-500">
                    <Camera className="w-4 h-4 text-black" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  Upload a professional photo to build trust with prospects
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <Input
                    value={profile.full_name || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <Input
                    value={profile.email || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    type="email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country/Region *
                  </label>
                  <Select
                    value={profile.country || ''}
                    onValueChange={(value) => setProfile(prev => ({ ...prev, country: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <Input
                    value={profile.phone || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    type="tel"
                  />
                </div>

                <Button 
                  onClick={() => updateProfile({
                    full_name: profile.full_name,
                    email: profile.email,
                    country: profile.country,
                    phone: profile.phone
                  })}
                  disabled={saving}
                  className="w-full bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Basic Info'}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Business Information */}
        {activeTab === 'business' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Business Category & Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Category
                  </label>
                  <Select
                    value={profile.business_category || ''}
                    onValueChange={(value) => setProfile(prev => ({ ...prev, business_category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your business category" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name
                  </label>
                  <Input
                    value={profile.business_name || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Your Business Name LLC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tagline/Slogan
                  </label>
                  <Input
                    value={profile.tagline || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="Your unique value proposition"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    About Me/Bio
                  </label>
                  <Textarea
                    value={profile.bio || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell prospects about yourself and your expertise..."
                    rows={4}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {(profile.bio || '').length}/500 characters
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profile Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center space-x-3 mb-3">
                    <Avatar>
                      <AvatarImage src={profile.profile_photo_url} />
                      <AvatarFallback>{profile.full_name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold">{profile.full_name || 'Your Name'}</h3>
                      <p className="text-sm text-gray-600">
                        {profile.business_name || 'Your Business'}
                      </p>
                    </div>
                  </div>
                  
                  {profile.tagline && (
                    <p className="text-sm font-medium text-blue-600 mb-2">
                      "{profile.tagline}"
                    </p>
                  )}
                  
                  {profile.bio && (
                    <p className="text-sm text-gray-700">
                      {profile.bio}
                    </p>
                  )}
                  
                  <div className="flex items-center space-x-2 mt-3">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {countries.find(c => c.value === profile.country)?.label || 'Location'}
                    </span>
                  </div>
                </div>

                <Button 
                  onClick={() => updateProfile({
                    business_category: profile.business_category,
                    business_name: profile.business_name,
                    tagline: profile.tagline,
                    bio: profile.bio
                  })}
                  disabled={saving}
                  className="w-full mt-4 bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Business Info'}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Social Links */}
        {activeTab === 'social' && (
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Social Media & Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {socialPlatforms.map((platform) => (
                  <div key={platform.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <platform.icon className="w-4 h-4 inline mr-2" />
                      {platform.label}
                    </label>
                    <Input
                      value={profile[platform.key] || ''}
                      onChange={(e) => setProfile(prev => ({ ...prev, [platform.key]: e.target.value }))}
                      placeholder={platform.placeholder}
                      type="url"
                    />
                  </div>
                ))}

                <Button 
                  onClick={() => updateProfile({
                    website_url: profile.website_url,
                    facebook_url: profile.facebook_url,
                    instagram_url: profile.instagram_url,
                    linkedin_url: profile.linkedin_url,
                    twitter_url: profile.twitter_url,
                    youtube_url: profile.youtube_url
                  })}
                  disabled={saving}
                  className="w-full bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Social Links'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Privacy Settings */}
        {activeTab === 'privacy' && (
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Privacy & Visibility Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Show Phone Number</h4>
                      <p className="text-sm text-gray-600">Allow team members to see your phone number</p>
                    </div>
                    <Switch
                      checked={profile.phone_visible || false}
                      onCheckedChange={(checked) => setProfile(prev => ({ ...prev, phone_visible: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Show Timezone</h4>
                      <p className="text-sm text-gray-600">Display your timezone for better coordination</p>
                    </div>
                    <Switch
                      checked={profile.timezone_visible || false}
                      onCheckedChange={(checked) => setProfile(prev => ({ ...prev, timezone_visible: checked }))}
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => updateProfile({
                    phone_visible: profile.phone_visible,
                    timezone_visible: profile.timezone_visible
                  })}
                  disabled={saving}
                  className="w-full bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Privacy Settings'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberProfile;